/* A coaching card as a RECORD, not a query result.
 *
 * Today a card exists only while it qualifies: the habit has to still score
 * badly and a better alternative has to still beat it by 15 points. So the
 * moment the user actually fixes the habit, the card **stops qualifying and
 * silently disappears**. The single most motivating event in the whole
 * product — *you fixed this leak* — is structurally unobservable, and the
 * evidence of the work is deleted by the work succeeding.
 *
 * Three things follow from making it a record:
 *
 *   1. **The id may not encode the answer.** It used to be
 *      `mistakes:${h}:${bestSan}`, so the day a different alternative became
 *      best-scoring, the id changed, a "new" card appeared for the same
 *      position, and every Leitner box earned on the old id was orphaned —
 *      and kept forever, because mergeLearn never pruned. `migrateCardIds`
 *      folds those in; it is mandatory, not optional, because this repo's
 *      stated philosophy is never lose progress.
 *   2. **Disappearing has to be explained.** A card that leaves the deck was
 *      either fixed or stopped occurring, and those are opposite outcomes.
 *      The miner now reports back on watched positions whether or not they
 *      still qualify (`followUp`), so the difference is measured rather than
 *      guessed.
 *   3. **Content has to be stored, not just scheduling.** `ls.cards` holds
 *      `{ box, dueAt, hits, lapses }` and nothing else, so a signed-in user on
 *      a fresh device has Leitner state for cards whose content cannot be
 *      reconstructed until their whole game history hydrates.
 *
 * Pure: state in, state out. No catalog, no React, no persistence.
 */

/* `share` — how often a move is chosen here — not `rate`, which is how well it
 * scores. A habit changes when the share changes; the score rate can move for
 * reasons that have nothing to do with the user. */
const IMPROVED_BY = 0.1;

/* Below this the position simply is not happening any more in the coaching
 * window: the leak was not fixed, it stopped being observable. Saying "fixed"
 * here would be the app taking credit for a user who changed openings. */
const STALE_BELOW = 4;

/* The record rides `sync.js` into a single Firestore document alongside every
 * Leitner box, so it cannot grow forever. Only resolved records are ever
 * dropped, oldest first — an active leak is never pruned to make room. */
export const RECORD_CAP = 120;

export const STATUSES = ["active", "improving", "fixed", "stale"];

export const positionHashOf = (cardId) => (String(cardId).startsWith("mistakes:") ? String(cardId).split(":")[1] : null);
export const stableIdOf = (cardId) => {
  const h = positionHashOf(cardId);
  return h ? `mistakes:${h}` : cardId;
};

/* Combine two Leitner entries for the same position. Same rule mergeLearn
 * uses for two devices' copies of one card — deepest box wins, and the most
 * generous due date — because collapsing two ids is the same question as
 * merging two devices: which of these represents more earned progress. */
function keepBetter(a, b) {
  if (!a) return b;
  if (!b) return a;
  return {
    box: Math.max(a.box || 0, b.box || 0),
    dueAt: Math.max(a.dueAt || 0, b.dueAt || 0),
    hits: Math.max(a.hits || 0, b.hits || 0),
    lapses: Math.max(a.lapses || 0, b.lapses || 0),
    missStreak: Math.min(a.missStreak || 0, b.missStreak || 0),
  };
}

/* E1's migration. Legacy `mistakes:${h}:${san}` ids fold into `mistakes:${h}`,
 * taking the deepest box where two of them collapse into one — which is the
 * normal case, not an edge case, since a changing recommendation is exactly
 * what minted the second id.
 *
 * Non-mistake ids (course lines, `courseId:lineId:ply`) are untouched: they
 * never carried a recommendation and their ids were always stable.
 *
 * Returns { cards, migrated } — `migrated` is how many legacy ids were folded,
 * so the caller can decide whether a write is even needed. */
export function migrateCardIds(cards = {}) {
  const out = {};
  let migrated = 0;
  for (const [id, state] of Object.entries(cards)) {
    const parts = String(id).split(":");
    const legacy = parts[0] === "mistakes" && parts.length > 2;
    const key = legacy ? `mistakes:${parts[1]}` : id;
    if (legacy) migrated += 1;
    out[key] = keepBetter(out[key], state);
  }
  return { cards: out, migrated };
}

/* What the record needs to ask the miner about: every position it is tracking,
 * and which move it told the user to play there. */
export function watchList(record = {}) {
  const watch = {};
  for (const [id, r] of Object.entries(record)) {
    const h = positionHashOf(id);
    if (h) watch[h] = r?.expected?.san || "";
  }
  return watch;
}

const snapshotOf = (card) => ({
  fen: card.fen,
  context: card.context,
  orientation: card.orientation,
  courseId: card.courseId ?? null,
  lineId: card.lineId ?? null,
  habit: card.habit ? { ...card.habit } : null,
  expected: card.alt ? { ...card.alt } : null,
  costCp: Number.isFinite(card.cpLoss) ? card.cpLoss : null,
  gamesSeen: card.seen ?? card.habit?.n ?? 0,
  // The engine's own pick when it beats the in-repertoire fix (grader.js) —
  // the "worth learning" rung above "make it a habit".
  better: card.better?.san ? { ...card.better } : null,
});

/* Reconcile the stored record against a freshly mined deck and the miner's
 * follow-up on everything it already knew about.
 *
 * The transitions are the point, so they are stated rather than inferred:
 *
 *   in the deck            -> active, or improving if the user is now choosing
 *                             the recommended move meaningfully more often
 *                             than when the card was raised
 *   gone, position active  -> fixed. Measured: the leak no longer qualifies
 *                             AND the position still occurs often enough to
 *                             say so.
 *   gone, position quiet   -> stale. Not the same thing as fixed and must
 *                             never be reported as it — this is someone who
 *                             changed openings, not someone who improved.
 *   gone, nothing known    -> left alone. No games loaded yet (a fresh device
 *                             before the history hydrates) must not retire
 *                             every card the user has.
 *
 * `timesFixed` counts, rather than latches, because a fixed habit can come
 * back — and coming back is worth knowing about.
 */
export function syncRecords(record = {}, { deck = [], followUp = null, now = Date.now() } = {}) {
  const out = {};
  for (const [id, r] of Object.entries(record)) out[id] = { ...r };
  const inDeck = new Set();

  for (const card of deck) {
    const id = stableIdOf(card.id);
    inDeck.add(id);
    const prev = out[id];
    const snap = snapshotOf(card);
    const raisedShare = prev?.raisedExpectedShare ?? snap.expected?.share ?? 0;
    const nowShare = snap.expected?.share ?? 0;
    out[id] = {
      ...prev,
      ...snap,
      raisedAt: prev?.raisedAt ?? now,
      // Kept from the raise so "improving" measures movement rather than the
      // position's baseline — otherwise a card raised while the user already
      // played the better move sometimes would read as improving forever.
      raisedExpectedShare: raisedShare,
      updatedAt: now,
      status: nowShare >= raisedShare + IMPROVED_BY ? "improving" : "active",
      fixedAt: prev?.fixedAt ?? null,
      timesFixed: prev?.timesFixed || 0,
    };
  }

  if (followUp) {
    for (const [id, r] of Object.entries(out)) {
      if (inDeck.has(id)) continue;
      const h = positionHashOf(id);
      const fu = h ? followUp[h] : null;
      if (!fu) continue; // nothing measured — say nothing
      if (fu.qualifies) continue; // still a live leak, just below the deck cap (E6)
      const stale = fu.n < STALE_BELOW;
      const wasFixed = r.status === "fixed";
      out[id] = {
        ...r,
        updatedAt: now,
        status: stale ? "stale" : "fixed",
        gamesSeen: fu.n,
        habit: fu.habit || r.habit,
        // The current standing on the move we recommended — what B4's
        // follow-up sentence reads from.
        expected: fu.expected ? { ...r.expected, ...fu.expected } : r.expected,
        fixedAt: stale ? r.fixedAt : r.fixedAt ?? now,
        timesFixed: !stale && !wasFixed ? (r.timesFixed || 0) + 1 : r.timesFixed || 0,
      };
    }
  }

  return pruneRecords(out, RECORD_CAP);
}

/* Over the cap, drop the least useful RESOLVED records — never an active or
 * improving one. "Nothing is silently deleted" is the goal; a hard ceiling on
 * a synced document is the constraint. Oldest resolution first, so what goes
 * is the thing longest since it mattered. */
export function pruneRecords(record, cap = RECORD_CAP) {
  const ids = Object.keys(record);
  if (ids.length <= cap) return record;
  const resolved = ids
    .filter((id) => record[id].status === "fixed" || record[id].status === "stale")
    .sort((a, b) => (record[a].updatedAt || 0) - (record[b].updatedAt || 0));
  const out = { ...record };
  for (const id of resolved.slice(0, ids.length - cap)) delete out[id];
  return out;
}

/* Everything the record knows, newest first, for the browsable surface (E4)
 * and for counting. Live leaks first — a fixed card is a trophy, an active one
 * is work. */
const ORDER = { active: 0, improving: 1, fixed: 2, stale: 3 };
export function recordList(record = {}, { status = null } = {}) {
  return Object.entries(record)
    .map(([id, r]) => ({ id, ...r }))
    .filter((r) => !status || r.status === status)
    .sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9) || (b.updatedAt || 0) - (a.updatedAt || 0));
}

export function recordCounts(record = {}) {
  const counts = { active: 0, improving: 0, fixed: 0, stale: 0 };
  for (const r of Object.values(record)) if (r.status in counts) counts[r.status] += 1;
  return counts;
}

/* Cards that transitioned to fixed on THIS reconcile — the payoff event the
 * product currently throws away (E5), and the opening line of the next
 * briefing (B4). Compares before/after rather than reading a flag, so it
 * cannot fire twice for the same transition. */
export function newlyFixed(before = {}, after = {}) {
  return Object.keys(after).filter((id) => after[id].status === "fixed" && before[id] && before[id].status !== "fixed");
}
